import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { api, errorMessage } from '../api'
import { Icon } from '../components/Icon'
import { PLAN_PRICE_EUR } from '../lib/company'
import { Banner, Field, Logo, Spinner } from '../components/ui'
import { normalizeWebsiteInput, storePendingWebsite, websiteHost } from '../lib/website'

// Light check only; the Worker validates properly. Kept zod-free so the login chunk stays small.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function Login() {
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  // From the landing page form. Re-validated here; an invalid value is dropped silently.
  const rawWebsite = params.get('website')
  const website = useMemo(() => (rawWebsite ? normalizeWebsiteInput(rawWebsite) : null), [rawWebsite])
  useEffect(() => {
    if (website) storePendingWebsite(website) // same-browser fallback if the server copy is missing
  }, [website])
  const login = useMutation({ mutationFn: (e: string) => api.login(e, website) })
  const expired = params.get('error') === 'expired'
  const otherError = params.get('error') && !expired

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    const value = email.trim()
    if (!EMAIL.test(value)) {
      setFieldError('Enter an email address like mira@studiomira.de')
      return
    }
    setFieldError(null)
    login.mutate(value)
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="px-4 py-5 sm:px-8">
        <Logo />
      </header>
      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-8 sm:items-center sm:pt-0">
        <div className="card w-full max-w-[440px] p-6 sm:p-8">
          {login.isSuccess ? (
            <div className="flex flex-col gap-4" aria-live="polite">
              <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-accent-soft text-accent">
                <Icon name="mail" size={20} />
              </span>
              <h1 className="font-head text-[26px] font-semibold tracking-[-0.6px]">Check your inbox</h1>
              <p className="text-[15px] leading-6 text-muted">
                If <strong className="font-semibold text-ink">{email.trim()}</strong> can sign in, a link is on its way. It works for 15
                minutes and only once. Open it on this device.
              </p>
              <p className="hint">Nothing after a few minutes? Check spam, or send a new link.</p>
              <div className="flex flex-wrap gap-3 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => login.mutate(email.trim())} disabled={login.isPending}>
                  Send a new link
                </button>
                <button type="button" className="btn-link btn" onClick={() => login.reset()}>
                  Use a different email
                </button>
              </div>
            </div>
          ) : (
            <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
              <div className="flex flex-col gap-2">
                {website && (
                  <p className="inline-flex items-center gap-2 self-start rounded-full bg-accent-soft px-3 py-1 text-[13px] font-semibold text-accent">
                    <Icon name="sparkles" size={13} />
                    Setting up Lobby for {websiteHost(website)}
                  </p>
                )}
                <h1 className="font-head text-[26px] font-semibold tracking-[-0.6px]">Sign in or start your trial</h1>
                <p className="text-[15px] leading-6 text-muted">
                  We email you a sign-in link. No password. New here? The same link creates your account.
                </p>
              </div>
              {expired && (
                <Banner tone="warn" title="That link has expired">
                  Sign-in links work for 15 minutes and only once. Enter your email for a new one.
                </Banner>
              )}
              {otherError && <Banner tone="error">That sign-in link didn't work. Enter your email for a new one.</Banner>}
              {login.isError && <Banner tone="error">{errorMessage(login.error)}</Banner>}
              <Field label="Work email" error={fieldError}>
                {({ id, describedBy, invalid }) => (
                  <input
                    id={id}
                    className="input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    placeholder="mira@studiomira.de"
                  />
                )}
              </Field>
              <button type="submit" className="btn btn-primary btn-lg" disabled={login.isPending}>
                {login.isPending ? <Spinner /> : <Icon name="mail" size={16} />}
                Email me a sign-in link
              </button>
              <p className="hint">
                7 days free. You add a card later in setup; the first EUR {PLAN_PRICE_EUR} is charged on day 8. Cancel any time before and you pay
                nothing.
              </p>
              <p className="hint">
                By continuing you accept the{' '}
                <Link to="/terms" className="underline underline-offset-2">
                  terms of service
                </Link>{' '}
                and{' '}
                <Link to="/dpa" className="underline underline-offset-2">
                  data processing agreement
                </Link>
                , and confirm you read the{' '}
                <Link to="/privacy" className="underline underline-offset-2">
                  privacy policy
                </Link>
                .
              </p>
            </form>
          )}
        </div>
      </main>
      <footer className="px-4 pb-6 text-center text-[13px] text-muted">
        <Link to="/" className="underline underline-offset-2">
          Back to the homepage
        </Link>
      </footer>
    </div>
  )
}
